import { useState, useEffect } from "react"
import { getPersonalCustomerDetails, updatePersonalCustomerDetails } from "../../../../../../global/paywall/paywallApiUrls";
import * as axios from "axios";
import * as classNames from "classnames"
import { SettingLinks } from "../calcalistPaywallCheckout";
import { PaywallAuthErrorCodes } from "../../../../../../global/paywall/paywallInterfaces";
import { CustomerDetails, DetailUpdateError } from "../../../../../../global/paywall/useDetailsUpdate";
interface CalcalistPaywallCheckoutPersonalDetailsProps {
    nextStep: () => void
    prevStep: () => void
    settingLinks: SettingLinks
    setCustomerDetails: (value: CustomerDetails) => void
    customerDetails: CustomerDetails
    agreeToTermsOfUse: boolean
    setAgreeToTermsOfUse: (value: boolean) => void
    error: DetailUpdateError
    clearError: () => void
    saveCustomerDetails: () => Promise<boolean>

}

const validationSchema = {
    email: {
        required: false,
        message: "שדה חובה",
        validate: (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) || "כתובת מייל לא תקינה",
    },
    firstName: {
        required: true,
        message: "שדה חובה",
        validate: (value: string) => {
            if (value.length > 50) return "שם פרטי יכול להכיל עד 50 תווים";
            return /^[A-Za-zא-ת\u0590-\u05FF' ]{2,}$/u.test(value) || "שם פרטי יכול להכיל רק אותיות";
        },
    },
    lastName: {
        required: true,
        message: "שדה חובה",
        validate: (value: string) => {
            if (value.length > 50) return "שם משפחה יכול להכיל עד 50 תווים";
            return /^[A-Za-zא-ת\u0590-\u05FF' ]{2,}$/u.test(value) || "שם משפחה יכול להכיל רק אותיות";
        },
    },
    phoneNumber: {
        required: true,
        message: "שדה חובה",
        validate: (value: string) => {
            if (isNaN(Number(value))) {
                return "מספר טלפון חייב להכיל ספרות בלבד";
            }
            return /^05\d{8}$/.test(value) || "מספר טלפון לא תקין"
        }
    },
    personalId: {
        required: true,
        message: "שדה חובה",
        validate: (value: string) => {
            if (isNaN(Number(value))) {
                return "מספר תעודת זהות חייב להכיל ספרות בלבד"
            }
            const isValidIsraeliID = (id: string): boolean => {
                if (!/^\d{9}$/.test(id)) return false;
                const idArray = id.split("").map(Number);
                let sum = 0;
                for (let i = 0; i < 9; i++) {
                    let num = idArray[i] * (i % 2 === 0 ? 1 : 2);
                    sum += num > 9 ? num - 9 : num;
                }
                return sum % 10 === 0;
            };

            return isValidIsraeliID(value) || "תעודת זהות לא תקינה";
        }
    },
    termsOfuse: {
        required: true,
        message: "יש לאשר תנאי שימוש להמשך תהליך הרכישה",
        validate: (value: boolean) => value || "יש לאשר תנאי שימוש להמשך תהליך הרכישה",
    }

};


const CalcalistPaywallCheckoutMobilePersonalDetails = (props: CalcalistPaywallCheckoutPersonalDetailsProps) => {
    const { nextStep, settingLinks: { terms, privacy, contactUsPage }, error, clearError, setCustomerDetails, customerDetails, setAgreeToTermsOfUse, saveCustomerDetails, agreeToTermsOfUse } = props;

    const fieldConfig = {
        email: { id: 1, name: "email", type: "text", label: "משתמש", disabled: true, order: 1, value: "" },
        firstName: { id: 2, name: "firstName", type: "text", label: "שם פרטי", order: 2, value: "" },
        lastName: { id: 3, name: "lastName", type: "text", label: "שם משפחה", order: 3, value: "" },
        personalId: { id: 5, name: "personalId", type: "text", label: "ת.ז", value: "" },
        phoneNumber: { id: 4, name: "phoneNumber", type: "text", label: "נייד", value: "" },
        termsOfuse: {
            id: 6, name: "termsOfuse", type: "checkbox",
            label: `מסכים/ה ל<a href="${terms.url}" target="_blank">תנאי השימוש</a>, <a href="${privacy.url}" target="_blank">מדיניות פרטיות</a>, וקבלת פניות שיווקיות כולל טלפוניות. הודעות תפעוליות ימשיכו להישלח בתקופת המינוי גם לאחר הסרתי מרשימת מדוורים.`,
            value: false
        },
    };

    const [errors, setErrors] = useState({});
    const getContactUsUrlWithParams = (formData) => {
        try {
            const contactPageUrl = contactUsPage.url || 'https://www.calcalist.co.il/';
            const url = new URL(contactPageUrl);
            const params = url.searchParams;
            params.set('firstName', formData.firstname.value || '');
            params.set('email', formData.email.value || '');
            params.set('phone', formData.phone.value || '');
            params.set('subject', 'תקלה טכנית');
            params.set('message', 'הוקלדה תעודת זהות הקיימת בחומת תשלום');
            return url.toString();
        } catch (e) {
            console.error('', e);
            return '';
        }
    };

    const errorMessagesServer: Partial<Record<DetailUpdateError, (formData: any) => string>> = {
        [DetailUpdateError.EXISTING_PERSONAL_ID]: (formData) =>
            `ת"ז קיימת במערכת, יש לפנות ל<a target="_blank" href="${getContactUsUrlWithParams(formData)}">שירות לקוחות</a>`,
        [DetailUpdateError.UNKNOWN_ERROR]: () =>
            "אירעה שגיאה במערכת, אנא נסו שוב מאוחר יותר",
    };



    const validateField = (name, value) => {
        const schema = validationSchema[name];
        if (!schema) return "";
        if (schema.required) {
            if (typeof value === "boolean" && !value) return schema.message;
            if (typeof value === "string" && !value.trim()) return schema.message;
        }
        if (schema.validate) {
            return typeof schema.validate(value) === "boolean" ? "" : schema.validate(value);
        }
        return "";
    };


    const handleChange = (e) => {
        let { name, value } = e.target;

        if (name === "termsOfuse") {
            setAgreeToTermsOfUse(e.target.checked);
            setErrors({
                ...errors,
                [name]: validateField(name, e.target.checked),
            });
            return;
        }
        if (name === "personalId" && (error === DetailUpdateError.EXISTING_PERSONAL_ID || error === DetailUpdateError.UNKNOWN_ERROR)) {
            clearError();
        }

        if ((name === "personalId" || name === 'phoneNumberNumber') && isNaN(Number(value))) {
            value = value.replace(/\D/g, "");
            setErrors({
                ...errors,
                [name]: validateField(name, "abc"),
            });
            return;
        }

        setCustomerDetails({
            ...customerDetails,
            [name]: value,
        });
        setErrors({
            ...errors,
            [name]: validateField(name, value),
        });
    };



    const handleSubmit = async (e) => {
        e.preventDefault();

        let newErrors = {};
        let isValid = true;


        Object.keys(fieldConfig).forEach((key) => {
            let keyValue = key === 'termsOfuse' ? agreeToTermsOfUse : customerDetails[key];
            const errorMessage = validateField(key, keyValue);
            if (errorMessage) {
                newErrors[key] = errorMessage;
                isValid = false;
            }
        });

        setErrors(newErrors);

        if (!isValid) {
            console.log("Form has errors:", newErrors);
            return;
        }

        console.log("Submitting form:", error);


        const response = await saveCustomerDetails();
        if (response) {
            console.log("Form submitted successfully:", customerDetails);
            nextStep();
        }
    };

    return (
        <div className="CalcalistPaywallCheckoutPersonalDetails">
            <h1 className="title">הזינו פרטים אישיים</h1>
            <div className="user-email">{fieldConfig['email'].value}</div>
            <div className="personal-details-form">
                <form id="checkout-personal-details" onSubmit={handleSubmit}>
                    {Object.keys(fieldConfig).map((field) => {
                        const inputField = fieldConfig[field];
                        return <div
                            className={`input-field ${classNames({ 'terms': inputField.name === 'termsOfuse' })}`}
                            key={inputField.id}>
                            <input
                                placeholder={inputField.label}
                                disabled={inputField.disabled}
                                type={inputField.type}
                                inputMode={inputField.inputmode}
                                pattern={inputField.pattern}
                                name={inputField.name}
                                value={inputField.name === 'termsOfuse' ? agreeToTermsOfUse : customerDetails[inputField.name]}
                                onChange={handleChange}
                                className={errors[field] ? "error" : ""}
                                hidden={inputField.name === "email"}
                            />
                            {inputField.name === 'termsOfuse' && <label dangerouslySetInnerHTML={{ __html: inputField.label }}></label>}
                            {errors[field] && <span className="input-field-error">{errors[field]}</span>}
                        </div>
                    })}

                    <div className="general-error" dangerouslySetInnerHTML={{
                        __html:
                          errorMessagesServer[error]
                            ? errorMessagesServer[error](customerDetails)
                            : ''
                    }}>
                    </div>
                    <button form="checkout-personal-details" className="submit-button" type="submit">המשך</button>
                </form>
            </div>

        </div>
    );
};

export default CalcalistPaywallCheckoutMobilePersonalDetails;